import { useState, useEffect, useContext } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import {
  Building2, Users, UserPlus, Mail, Shield,
  Trash2, XCircle, Clock
} from 'lucide-react';

const CompanySettings = () => {
  const API = useContext(ApiContext);
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Vérifier si l'utilisateur est manager/admin
  const isManager = ['admin', 'manager', 'generalAdmin'].includes(userData?.role);

  useEffect(() => {
    if (userData?.companyId) {
      fetchCompanyData();
      fetchMembers();
      if (isManager) {
        fetchInvitations();
      }
    }
    setLoading(false);
  }, [userData]);

  const fetchCompanyData = async () => {
    try {
      const response = await API.get(`/api/companies/${userData.companyId}`);
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company:', error);
      toast.error('Erreur lors du chargement de l\'entreprise');
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await API.get(`/api/companies/${userData.companyId}/members`);
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await API.get('/api/invitations/company');
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleSendInvitations = async (e) => {
    e.preventDefault();

    if (!inviteEmails.trim()) {
      toast.error('Veuillez entrer au moins un email');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await API.post('/api/invitations/send', {
        emails: inviteEmails,
        role: inviteRole
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setInviteEmails('');
        setInviteRole('user');
        fetchInvitations();
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi des invitations');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer cette invitation ?')) {
      return;
    }

    try {
      await API.delete(`/api/invitations/${invitationId}`);
      toast.success('Invitation révoquée');
      fetchInvitations();
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast.error('Erreur lors de la révocation');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await API.patch(`/api/companies/members/${userId}/role`, { role: newRole });
      toast.success('Rôle mis à jour');
      fetchMembers();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification du rôle');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${userName} de l'entreprise ?`)) {
      return;
    }

    try {
      await API.delete(`/api/companies/members/${userId}`);
      toast.success('Membre retiré de l\'entreprise');
      fetchMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du retrait du membre');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!userData?.companyId) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <Building2 className="mx-auto mb-3 text-yellow-600" size={48} />
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">
          Aucune entreprise associée
        </h3>
        <p className="text-sm text-yellow-700">
          Vous devez faire partie d'une entreprise pour accéder à cette section.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Informations entreprise */}
      <div className="bg-white rounded-xl p-6  border border-gray-200">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Building2 className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800 m-0">
              {companyData?.name || userData.company}
            </h2>
            <p className="text-sm text-slate-500">
              {members.length} membre{members.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire d'invitation (managers/admins uniquement) */}
      {isManager && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <UserPlus className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 m-0">Inviter des collaborateurs</h3>
              <p className="text-sm text-slate-600">
                Séparez les emails par des virgules ou des lignes
              </p>
            </div>
          </div>

          <form onSubmit={handleSendInvitations} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Mail size={16} className="inline mr-2" />
                Adresses email
              </label>
              <textarea
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm placeholder-slate-400"
                placeholder="email1@example.com, email2@example.com&#10;email3@example.com"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Shield size={16} className="inline mr-2" />
                Rôle par défaut
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm"
              >
                <option value="user">Utilisateur</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={sendingInvite}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-4 focus:ring-blue-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {sendingInvite ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <Mail size={18} />
                  <span>Envoyer les invitations</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Liste des membres */}
      <div className="bg-white rounded-xl p-6  border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center m-0">
            <Users size={20} className="mr-2 text-slate-600" />
            Membres de l'équipe
          </h3>
          <span className="bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            {members.length} actif{members.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Mobile - Cards */}
        <div className="block lg:hidden space-y-3">
          {members.map(member => (
            <div key={member._id} className="bg-gradient-to-r from-white to-gray-50 p-4 rounded-xl border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{member.name}</p>
                    <p className="text-xs text-slate-500">{member.email}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {isManager && member._id !== userData.id ? (
                  <>
                    <select
                      value={member.role}
                      onChange={(e) => handleChangeRole(member._id, e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border-2 border-gray-200 rounded-lg"
                    >
                      <option value="user">Utilisateur</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => handleRemoveMember(member._id, member.name)}
                      className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={16} />
                      <span className="text-sm">Retirer</span>
                    </button>
                  </>
                ) : (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                    ${member.role === 'admin' ? 'bg-green-100 text-green-800' :
                      member.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'}`}>
                    <Shield size={12} className="mr-1" />
                    {member.role}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop - Table */}
        <div className="hidden lg:block overflow-hidden rounded-xl border border-gray-200">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  Membre
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  Rôle
                </th>
                {isManager && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {members.map(member => (
                <tr key={member._id} className="hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="h-9 w-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {member.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isManager && member._id !== userData.id ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleChangeRole(member._id, e.target.value)}
                        className="px-3 py-1.5 text-sm bg-white border-2 border-gray-200 rounded-lg"
                      >
                        <option value="user">Utilisateur</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                        ${member.role === 'admin' ? 'bg-green-100 text-green-800' :
                          member.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'}`}>
                        <Shield size={12} className="mr-1" />
                        {member.role}
                      </span>
                    )}
                  </td>
                  {isManager && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {member._id !== userData.id && (
                        <button
                          onClick={() => handleRemoveMember(member._id, member.name)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invitations en attente (managers/admins uniquement) */}
      {isManager && invitations.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center m-0">
              <Clock size={20} className="mr-2 text-slate-600" />
              Invitations en attente
            </h3>
            <span className="bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
              {invitations.length} en attente
            </span>
          </div>

          <div className="space-y-2">
            {invitations.map(invitation => (
              <div key={invitation._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{invitation.email}</p>
                  <p className="text-xs text-slate-500">
                    Invité par {invitation.invitedBy.name} •
                    Expire le {new Date(invitation.expiresAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <button
                  onClick={() => handleRevokeInvitation(invitation._id)}
                  className="ml-4 text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                >
                  <XCircle size={20} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySettings;
